import { useOptionalAuth } from "@/app/auth";
import { useMkRoot } from "@/app/branding";
import { MkIcon } from "@/mk/MkIcon";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "./useMediaQuery";

/** A column never shrinks past this: narrower would hide its own handle. */
export const MIN_COL_WIDTH = 56;
const MAX_COL_WIDTH = 900;

function clampWidth(value: number) {
    return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, Math.round(value)));
}

/** Pixel widths the reader sets by dragging a header edge. Nothing is stored:
 *  every visit starts from the default sizes again. */
export function useColumnWidths() {
    const [widths, setWidths] = useState<Record<string, number>>({});

    function startResize(key: string) {
        return (event: ReactMouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            const startX = event.clientX;
            const head = (event.currentTarget as HTMLElement).closest("th");
            const start = widths[key] ?? head?.getBoundingClientRect().width ?? 120;
            const rtl = document.dir === "rtl";
            const move = (point: MouseEvent) => {
                const delta = rtl ? startX - point.clientX : point.clientX - startX;
                setWidths((current) => ({ ...current, [key]: clampWidth(start + delta) }));
            };
            const stop = (point: MouseEvent) => {
                const delta = rtl ? startX - point.clientX : point.clientX - startX;
                setWidths((current) => ({ ...current, [key]: clampWidth(start + delta) }));
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", stop);
            };
            window.addEventListener("mousemove", move);
            window.addEventListener("mouseup", stop);
        };
    }

    return { widths, startResize };
}

/** Hidden field IDs only: no row data or translated labels enter storage. */
function readHiddenColumns(storageKey: string | null): string[] | null {
    if (!storageKey) return null;
    try {
        const value: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "null");
        return Array.isArray(value) ? value.filter((key): key is string => typeof key === "string") : null;
    } catch { return null; }
}

/** Explicit table identity keeps identical column schemas in different tables
 * independent. Filtering at render time leaves new fields visible by default. */
export function useHiddenColumns(keys: string[], tableId?: string, defaults: string[] = []) {
    const userId = useOptionalAuth()?.user?.id;
    const storageKey = userId && tableId ? `promaster.table.columns.v1:${userId}:${tableId}` : null;
    const [state, setState] = useState(() => ({ storageKey, keys: readHiddenColumns(storageKey) ?? defaults }));
    const signature = JSON.stringify(keys);
    const defaultSignature = JSON.stringify(defaults);
    const current = state.storageKey === storageKey ? state.keys : readHiddenColumns(storageKey) ?? defaults;
    const hidden = Object.fromEntries(current.filter((key) => keys.includes(key)).map((key) => [key, true]));
    const live = useRef({ storageKey, current, keys });
    live.current = { storageKey, current, keys };

    useEffect(() => {
        setState({ storageKey, keys: readHiddenColumns(storageKey) ?? defaults });
        const sync = (event: StorageEvent) => {
            if (storageKey && (event.key === storageKey || event.key === null)) {
                setState({ storageKey, keys: readHiddenColumns(storageKey) ?? defaults });
            }
        };
        window.addEventListener("storage", sync);
        return () => window.removeEventListener("storage", sync);
    }, [storageKey, defaultSignature]);

    function update(next: string[]) {
        const key = live.current.storageKey;
        live.current.current = next;
        setState({ storageKey: key, keys: next });
        try { if (key) localStorage.setItem(key, JSON.stringify(next)); } catch { /* session-only if storage is unavailable */ }
    }
    function toggleHidden(key: string) {
        const current = live.current.current.filter((item) => live.current.keys.includes(item));
        if (!live.current.keys.includes(key)) return;
        update(current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
    }
    // Keep anonymous / unkeyed widgets temporary, including on schema changes.
    useEffect(() => {
        if (!storageKey) {
            live.current.current = defaults;
            setState({ storageKey, keys: defaults });
        }
    }, [storageKey, signature, defaultSignature]);
    return { hidden, toggleHidden, resetHidden: () => update([]) };
}

export interface MenuColumn {
    key: string;
    label: string;
}

interface MenuProps {
    columns: MenuColumn[];
    hidden: Record<string, boolean>;
    onToggle(key: string): void;
    onReset(): void;
    buttonClassName?: string;
}

/** The eye of the table: which columns stay visible while the reader works. */
export function MkColumnsMenu({ columns, hidden, onToggle, onReset, buttonClassName }: MenuProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const wrap = useRef<HTMLDivElement>(null);
    const panel = useRef<HTMLDivElement>(null);
    const compact = useMediaQuery("(max-width: 1024px)");
    const root = useMkRoot();
    const place = useCallback(() => {
        if (!compact || !panel.current || !wrap.current) return;
        const rect = wrap.current.getBoundingClientRect();
        const viewport = window.visualViewport;
        const top = viewport?.offsetTop ?? 0, height = viewport?.height ?? window.innerHeight;
        const width = Math.min(280, window.innerWidth - 24);
        const room = Math.min(320, height - 24);
        Object.assign(panel.current.style, {
            left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`,
            top: `${Math.max(top + 12, Math.min(rect.bottom + 6, top + height - room - 12))}px`,
            width: `${width}px`, maxHeight: `${room}px`,
        });
    }, [compact]);
    useLayoutEffect(() => { if (open) place(); }, [open, place]);
    useEffect(() => {
        if (!open || !compact) return;
        const escape = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            event.preventDefault(); event.stopPropagation(); setOpen(false);
            wrap.current?.querySelector<HTMLButtonElement>("button")?.focus();
        };
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        window.addEventListener("keydown", escape, true);
        window.visualViewport?.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
            window.removeEventListener("keydown", escape, true);
            window.visualViewport?.removeEventListener("resize", place);
        };
    }, [open, compact, place]);

    useEffect(() => {
        if (!open) {
            return;
        }
        function away(event: MouseEvent) {
            if (wrap.current && !wrap.current.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        window.addEventListener("click", away);
        return () => window.removeEventListener("click", away);
    }, [open ]);

    const hiddenCount = columns.filter((column) => hidden[column.key]).length;
    const menu = open && (
                <div className="mk-cols-menu" role="group" aria-label={t("mk.table.columns")}>
                    {columns.map((column) => (
                        <label key={column.key} className="mk-cols-item">
                            <input type="checkbox" className="mk-table-checkbox"
                                   checked={!hidden[column.key]}
                                   onChange={() => onToggle(column.key)} />
                            <span>{column.label || column.key}</span>
                        </label>
                    ))}
                    {hiddenCount > 0 && (
                        <>
                            <div className="mk-table-menusep" />
                            <button type="button" className="mk-table-filteritem"
                                    onClick={() => {
                                        onReset();
                                        setOpen(false);
                                    }}>
                                <MkIcon name="fa-eye" className="mk-table-filtercheck" />
                                <span>{t("mk.table.showAll")}</span>
                            </button>
                        </>
                    )}
                </div>
    );
    return (
        <div className="mk-cols" ref={wrap}>
            <button type="button"
                    className={`${buttonClassName || "mk-table-filter"}${hiddenCount ? " mk-table-filter-active" : ""}`}
                    title={t("mk.table.columns")} aria-label={t("mk.table.columns")} aria-expanded={open}
                    onClick={(event) => {
                        event.stopPropagation();
                        setOpen((value) => !value);
                    }}>
                <MkIcon name="fa-columns" />
                {hiddenCount ? <span className="mk-table-filter-badge">{hiddenCount}</span> : null}
            </button>
            {compact && open ? createPortal(
                <div className="mk mk-cols-portal" {...root} ref={panel}
                     style={{ position: "fixed", zIndex: "calc(var(--mk-z-modal) + 10)" }}>{menu}</div>, document.body)
                : menu}
        </div>
    );
}

/** The grip at the edge of a header cell: dragging it widens or narrows
 *  that column the way a spreadsheet does. */
export function ThResizer({ onMouseDown }: { onMouseDown(event: ReactMouseEvent): void }) {
    return (
        <span className="mk-th-resizer" onMouseDown={onMouseDown}
              onClick={(event) => event.stopPropagation()} />
    );
}
